// Main Product Page Color Swatch Switcher
// - Switches full product info in place on swatch click
// - Updates URL path so reload preserves selection
// - Enforces no default size selection; user must pick a size before Add to Cart
// - Prefetches images from other color variants for instant loading
// - Enables Add to Cart button when user selects a size

(function () {
  const prefetchedImages = new Set();
  const prefetchedProducts = new Set();

  function getMainProductInfoEl() {
    return document.querySelector('product-info[data-main-product]');
  }

  // Immediately swap images before full product swap
  async function swapProductImagesImmediately(productUrl) {
    const productInfo = getMainProductInfoEl();
    if (!productInfo || !productUrl) return;

    try {
      // Fetch product page HTML to extract media
      const response = await fetch(productUrl);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the new product's media gallery
      const newProductInfo = doc.querySelector(
        'product-info[data-main-product]'
      );
      if (!newProductInfo) return;

      const newMediaGallery =
        newProductInfo.querySelector('[id^="MediaGallery"]') ||
        newProductInfo.querySelector('.main-product__media');

      if (!newMediaGallery) return;

      // Find current media gallery container
      const currentMediaGallery =
        productInfo.querySelector('[id^="MediaGallery"]') ||
        productInfo.querySelector('.main-product__media');

      if (!currentMediaGallery) return;

      // Find swiper-product-gallery and swiper-product-thumbs elements to reinitialize later
      const swiperGallery = currentMediaGallery.querySelector(
        'swiper-product-gallery'
      );
      const swiperThumbs =
        currentMediaGallery.parentElement?.querySelector(
          'swiper-product-thumbs'
        ) || currentMediaGallery.querySelector('swiper-product-thumbs');
      const wasGalleryInitialized = swiperGallery?.swiperInitialized;
      const wasThumbsInitialized = swiperThumbs?.swiperInitialized;

      // Immediately replace the entire media gallery innerHTML
      // This shows images right away (they're prefetched, so browser cache will serve them)
      // The full product swap will handle proper re-initialization later
      currentMediaGallery.innerHTML = newMediaGallery.innerHTML;

      // Remove target="_blank" from gallery toggle links
      const galleryToggleLinks = currentMediaGallery.querySelectorAll(
        'a.product__gallery-toggle'
      );
      galleryToggleLinks.forEach((link) => {
        link.removeAttribute('target');
      });

      // IMPORTANT: Thumbs must initialize BEFORE gallery (gallery needs thumbs instance)
      // First, reinitialize swiper thumbs if it was previously initialized
      // After replacing innerHTML, thumbs should be inside currentMediaGallery
      // (since main-product__media contains both gallery and thumbs)
      const newSwiperThumbs = currentMediaGallery.querySelector(
        'swiper-product-thumbs'
      );

      if (newSwiperThumbs && wasThumbsInitialized) {
        // Destroy existing thumbs instance if it exists
        if (
          newSwiperThumbs.instance &&
          typeof newSwiperThumbs.instance.destroy === 'function'
        ) {
          try {
            newSwiperThumbs.instance.destroy(true, true);
          } catch (e) {
            // Ignore errors
          }
        }
        // Reset initialization flag
        newSwiperThumbs.swiperInitialized = false;
        // Reinitialize thumbs - must do this BEFORE gallery reinitializes
        if (typeof newSwiperThumbs.init === 'function') {
          newSwiperThumbs.init();
        }
      }

      // Then, reinitialize swiper gallery after thumbs (gallery needs thumbs instance)
      const newSwiperGallery = currentMediaGallery.querySelector(
        'swiper-product-gallery'
      );
      if (newSwiperGallery && wasGalleryInitialized) {
        // Reset initialization flag
        newSwiperGallery.swiperInitialized = false;
        // Reinitialize swiper - wait a bit for thumbs to initialize first
        setTimeout(() => {
          if (typeof newSwiperGallery.init === 'function') {
            newSwiperGallery.init();
          }
        }, 100); // Give thumbs time to initialize
      }

      // Fade-in animation for newly inserted images
      const imgs = currentMediaGallery.querySelectorAll('img');
      imgs.forEach((img) => {
        img.classList.add('media-fade-in');
        const activate = () => {
          // ensure next frame to apply transition
          requestAnimationFrame(() => {
            img.classList.add('media-fade-in--active');
          });
        };
        if (img.complete) {
          activate();
        } else {
          img.addEventListener('load', activate, { once: true });
          img.addEventListener('error', activate, { once: true });
        }
      });
    } catch (error) {
      // Silently fail - images will load with full swap
    }
  }

  function swapMainProduct(productUrl) {
    const productInfo = getMainProductInfoEl();
    if (!productInfo || !productUrl) return;

    // Add loading state to product content area (excluding media)
    const productContent = productInfo.querySelector(
      '.product__content, .product-grid__item--content-wrapper'
    );
    if (productContent) {
      productContent.classList.add('product-page__content--loading');
    }

    // Immediately swap images before full product info loads
    swapProductImagesImmediately(productUrl).catch(() => {
      // If immediate image swap fails, continue with full swap
    });

    // Then proceed with full product swap for the rest of the info
    try {
      // Always fetch full page to get complete HTML, but only swap product-info element
      // This preserves sections like recommended products that are outside product-info
      const shouldFetchFullPage = productInfo.dataset.updateUrl === 'true';
      const requestUrl = productInfo.buildRequestUrlWithParams(
        productUrl,
        [],
        shouldFetchFullPage
      );

      // Store original callback to add cleanup
      // Pass false for updateFullPage so it only swaps product-info, not entire main
      // We'll handle URL update manually
      const originalCallback = productInfo.handleSwapProduct(
        productUrl,
        false // Only swap product-info element, preserve other sections
      );

      // Wrap callback to update URL and remove loading state when done
      const wrappedCallback = (htmlDoc) => {
        // Call original callback first to swap product-info element
        // This will swap only the product-info element, preserving other sections like recommended products
        const result = originalCallback(htmlDoc);

        // Update URL manually after swap
        // The original callback's updateURL was called, but we ensure it's updated here
        try {
          const newProductInfo = htmlDoc.querySelector(
            "product-info[id^='MainProduct'], product-info[data-main-product]"
          );

          if (
            newProductInfo &&
            productInfo &&
            typeof productInfo.updateURL === 'function'
          ) {
            const variantEl = newProductInfo.querySelector(
              'product-variant-selects [data-selected-variant]'
            );
            let variantId = null;
            if (variantEl && variantEl.innerHTML) {
              try {
                const variant = JSON.parse(variantEl.innerHTML);
                variantId = variant.id;
              } catch (e) {
                // Variant not available, use null
              }
            }
            productInfo.updateURL(productUrl, variantId);
          } else {
            // Fallback: use history.pushState
            window.history.pushState({ productUrl }, '', productUrl);
          }
        } catch (e) {
          // If parsing fails, still update URL
          window.history.pushState({ productUrl }, '', productUrl);
        }

        // Reinitialize media gallery after product swap
        // Remove loading state after view transition completes (HTMLUpdateUtility uses 500ms timeout)
        setTimeout(() => {
          // Find the content element again in case it was replaced
          const currentProductInfo = getMainProductInfoEl();
          const currentProductContent = currentProductInfo?.querySelector(
            '.product__content, .product-grid__item--content-wrapper'
          );
          if (currentProductContent) {
            currentProductContent.classList.remove(
              'product-page__content--loading'
            );
          }

          // Reinitialize swiper gallery and thumbs after product swap
          // IMPORTANT: Thumbs must initialize BEFORE gallery because gallery needs thumbs instance
          const mediaGallery = currentProductInfo?.querySelector(
            'swiper-product-gallery'
          );
          const mediaThumbs = currentProductInfo?.querySelector(
            'swiper-product-thumbs'
          );

          // First, reinitialize thumbs (gallery needs thumbs instance)
          if (mediaThumbs) {
            // Destroy existing thumbs instance if it exists
            if (mediaThumbs.swiperInitialized && mediaThumbs.instance) {
              try {
                mediaThumbs.instance.destroy(true, true);
              } catch (e) {
                // Ignore errors
              }
            }
            // Reset initialization flag
            mediaThumbs.swiperInitialized = false;

            // Reinitialize thumbs - this ensures thumbnails show up on mobile
            if (typeof mediaThumbs.init === 'function') {
              mediaThumbs.init();
            }
          }

          // Then reinitialize gallery (it needs thumbs to already be initialized)
          if (mediaGallery) {
            // Reset swiper gallery initialization flag and reinitialize
            if (mediaGallery.swiperInitialized && mediaGallery.instance) {
              // Destroy existing instance
              try {
                mediaGallery.instance.destroy(true, true);
              } catch (e) {
                // Ignore errors
              }
            }
            mediaGallery.swiperInitialized = false;

            // Wait a bit for thumbs to initialize first, then initialize gallery
            setTimeout(() => {
              // Reinitialize swiper gallery
              if (typeof mediaGallery.init === 'function') {
                mediaGallery.init();
              }

              // Reinitialize PhotoSwipe lightbox if zoom is enabled
              if (
                mediaGallery.hasAttribute('zoom') &&
                typeof PhotoSwipeLightbox !== 'undefined'
              ) {
                // Destroy existing PhotoSwipe instance if it exists
                if (mediaGallery._photoSwipeLightboxInstance) {
                  try {
                    mediaGallery._photoSwipeLightboxInstance.destroy();
                    mediaGallery._photoSwipeLightboxInstance = null;
                  } catch (e) {
                    // Ignore errors
                  }
                }

                // Reinitialize PhotoSwipe - this will attach to the new gallery elements
                if (typeof mediaGallery.photoSwipeLightboxInit === 'function') {
                  mediaGallery.photoSwipeLightboxInit();
                }
              }

              // Remove target="_blank" from gallery toggle links to prevent new tab opening
              // PhotoSwipe will handle the click events properly
              const galleryToggleLinks = mediaGallery.querySelectorAll(
                'a.product__gallery-toggle'
              );
              galleryToggleLinks.forEach((link) => {
                link.removeAttribute('target');
              });
            }, 50); // Small delay to ensure thumbs is initialized
          }
        }, 600); // Slightly longer than viewTransition's 500ms to ensure it's done
        return result;
      };

      productInfo.renderProductInfo({
        requestUrl,
        targetId: 'ProductSwatch',
        callback: wrappedCallback,
      });
    } catch (e) {
      // Remove loading state on error
      if (productContent) {
        productContent.classList.remove('product-page__content--loading');
      }
      // Fallback: navigate if the component API is unavailable
      window.location.href = productUrl;
    }
  }

  function enforceNoDefaultVariant(productInfoRoot) {
    const root = productInfoRoot || getMainProductInfoEl();
    if (!root) return;

    // SKIP for single-variant products - don't touch them at all
    const productForm = root.querySelector('product-form');
    const isSingleVariant = productForm?.hasAttribute('data-single-variant') ||
                           productForm?.dataset.singleVariant === 'true';
    
    if (isSingleVariant) {
      return; // Don't modify single-variant products - they're handled by Liquid
    }

    // Uncheck all radio inputs (size/buttons)
    root.querySelectorAll('fieldset input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    // Clear selected variant payload if present
    const selectedVariantEl = root.querySelector(
      'product-variant-selects [data-selected-variant]'
    );
    if (selectedVariantEl) selectedVariantEl.innerHTML = '';

    // Clear variant input to prevent adding wrong variant to cart
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = '';
      }
    }

    // Disable Add to Cart initially (will be enabled when variant is selected)
    if (productForm && typeof productForm.toggleSubmitButton === 'function') {
      productForm.toggleSubmitButton(true, 'SELECT A SIZE');
    } else {
      const submit = root.querySelector('product-form [type="submit"]');
      if (submit) {
        submit.setAttribute('disabled', 'disabled');
        submit.setAttribute('aria-disabled', 'true');
        const span = submit.querySelector('span');
        if (span) span.textContent = 'SELECT A SIZE';
      }
    }

    // Make the disabled "SELECT A SIZE" button black instead of grey (mobile)
    const submitBtn = root.querySelector('product-form .product-form__submit');
    if (submitBtn) {
      submitBtn.style.setProperty('--color-button-background', 'rgb(0, 0, 0)');
      submitBtn.style.setProperty('--color-button-text', 'rgb(255, 255, 255)');
      submitBtn.style.setProperty('--color-button-outline', 'rgb(0, 0, 0)');
      submitBtn.style.cursor = 'pointer';
    }
  }

  // Prefetch images from a product URL
  async function prefetchProductImages(productUrl) {
    if (!productUrl || prefetchedProducts.has(productUrl)) return;

    try {
      prefetchedProducts.add(productUrl);
      const response = await fetch(productUrl);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find product media gallery
      const mediaGallery =
        doc.querySelector('[id^="MediaGallery"]') ||
        doc.querySelector('.main-product__media') ||
        doc.querySelector('swiper-product-gallery');

      if (!mediaGallery) return;

      // Extract all image URLs
      const images = mediaGallery.querySelectorAll(
        'img[src], img[data-src], source[srcset], [data-media-id] img, [data-media-id] source'
      );

      images.forEach((img) => {
        let imageUrl = img.src || img.dataset.src;

        // Handle srcset attribute
        if (!imageUrl && img.srcset) {
          const srcset = img.getAttribute('srcset') || img.srcset;
          imageUrl = srcset.split(',')[0]?.trim().split(' ')[0];
        }

        // Handle source elements with srcset
        if (!imageUrl && img.tagName === 'SOURCE' && img.srcset) {
          const srcset = img.getAttribute('srcset');
          imageUrl = srcset?.split(',')[0]?.trim().split(' ')[0];
        }

        // Prefetch the image
        if (imageUrl && !prefetchedImages.has(imageUrl)) {
          const prefetchImage = new Image();
          prefetchImage.src = imageUrl;
          prefetchedImages.add(imageUrl);
        }
      });
    } catch (error) {
      // Silently fail prefetching - it's just an optimization
    }
  }

  // Prefetch images from all color swatch products
  function prefetchAllSwatchProductImages() {
    const swatches = document.querySelectorAll(
      '.product-page__color-swatch[data-product-url]'
    );

    // Prefetch images aggressively - start immediately for all swatches
    swatches.forEach((swatch, index) => {
      const productUrl = swatch.dataset.productUrl;
      if (productUrl) {
        // Stagger slightly to avoid overwhelming the network, but start quickly
        setTimeout(() => prefetchProductImages(productUrl), index * 50);
      }
    });
  }

  function onSwatchClick(event) {
    const swatch = event.target.closest('.product-page__color-swatch');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();

    const url = swatch.dataset.productUrl;
    if (!url) return;
    swapMainProduct(url);
  }

  // Cache for product variant data to avoid repeated fetches
  const variantDataCache = new Map();

  // Get variant data for a product URL
  async function getProductVariantData(productUrl) {
    if (variantDataCache.has(productUrl)) {
      return variantDataCache.get(productUrl);
    }

    try {
      const response = await fetch(productUrl);
      if (!response.ok) return null;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const variantSelects = doc.querySelector('product-variant-selects');
      if (!variantSelects) return null;

      const allVariantsEl = variantSelects.querySelector('[data-all-variants]');
      if (!allVariantsEl || !allVariantsEl.textContent) return null;

      const allVariants = JSON.parse(allVariantsEl.textContent);
      const optionsWithValues = Array.from(
        variantSelects.querySelectorAll('fieldset')
      ).map((fieldset) => {
        const legend = fieldset.querySelector('legend');
        const optionName = legend?.textContent?.trim() || '';
        return {
          name: optionName,
          position: Array.from(variantSelects.querySelectorAll('fieldset')).indexOf(fieldset) + 1,
        };
      });

      const data = {
        variants: allVariants,
        options: optionsWithValues,
      };

      variantDataCache.set(productUrl, data);
      return data;
    } catch (error) {
      console.debug('Error fetching variant data:', error);
      return null;
    }
  }

  // Update color swatch availability based on selected size
  async function updateColorSwatchAvailability(selectedSize, currentProductId) {
    const swatchesContainer = document.querySelector(
      '.product-page__color-swatches'
    );
    if (!swatchesContainer) return;

    const allSwatches = swatchesContainer.querySelectorAll(
      '.product-page__color-swatch'
    );
    if (allSwatches.length === 0) return;

    // Get current product's variant data to determine which option is size
    const productInfo = getMainProductInfoEl();
    if (!productInfo) return;

    const variantSelects = productInfo.querySelector('product-variant-selects');
    if (!variantSelects) return;

    const allVariantsEl = variantSelects.querySelector('[data-all-variants]');
    if (!allVariantsEl) return;

    let allVariants = [];
    try {
      allVariants = JSON.parse(allVariantsEl.textContent);
    } catch (e) {
      return;
    }

    // Find which option position is the size option
    // Look for the fieldset with "SELECT A SIZE" legend text
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    let sizeOptionPosition = null;
    let sizeFieldsetIndex = null;

    for (let i = 0; i < fieldsets.length; i++) {
      const fieldset = fieldsets[i];
      const legend = fieldset.querySelector('legend');
      const legendText = legend?.textContent?.trim() || '';
      
      // Check if this is the size fieldset (has "SELECT A SIZE" text)
      if (legendText.includes('SELECT A SIZE') || legendText.toUpperCase().includes('SIZE')) {
        sizeFieldsetIndex = i;
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          selectedSize = checkedInput.value;
        }
        break;
      }
    }

    // If we found the size fieldset, determine its position in the options array
    if (sizeFieldsetIndex !== null && selectedSize) {
      // Find a variant that has this size to determine the option position
      for (const variant of allVariants) {
        if (variant.options && variant.options.length > sizeFieldsetIndex) {
          const variantSizeValue = variant.options[sizeFieldsetIndex];
          if (variantSizeValue && variantSizeValue.toString().trim().toLowerCase() === selectedSize.toString().trim().toLowerCase()) {
            sizeOptionPosition = sizeFieldsetIndex + 1; // Options are 1-indexed
            break;
          }
        }
      }
      
      // Fallback: if we couldn't find a match, assume the fieldset index corresponds to option position
      if (!sizeOptionPosition) {
        sizeOptionPosition = sizeFieldsetIndex + 1;
      }
    } else if (selectedSize) {
      // Fallback: if we couldn't identify the size fieldset, try to find it by matching the selected size
      for (let i = 0; i < fieldsets.length; i++) {
        const fieldset = fieldsets[i];
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedInput && checkedInput.value === selectedSize) {
          sizeOptionPosition = i + 1;
          break;
        }
      }
    }

    // If no size selected, remove unavailable class from all swatches
    if (!sizeOptionPosition || !selectedSize) {
      allSwatches.forEach((swatch) => {
        // Only remove if it's not already unavailable for other reasons (like product sold out)
        const productId = swatch.dataset.productId;
        if (productId !== currentProductId) {
          // Check if the product itself is available
          const isProductUnavailable = swatch.classList.contains('product-page__color-swatch--unavailable') &&
            !swatch.dataset.sizeUnavailable; // Only remove if it was marked unavailable due to size
          if (isProductUnavailable) {
            // Keep unavailable if product is sold out, but remove size-specific marker
            swatch.removeAttribute('data-size-unavailable');
          } else {
            swatch.classList.remove('product-page__color-swatch--unavailable');
            swatch.removeAttribute('data-size-unavailable');
          }
        }
      });
      return;
    }

    // Update each swatch
    const updatePromises = Array.from(allSwatches).map(async (swatch) => {
      const productUrl = swatch.dataset.productUrl;
      const productId = swatch.dataset.productId;

      // Skip current product's swatch
      if (productId === currentProductId) {
        return;
      }

      if (!productUrl) {
        // If no URL, mark as unavailable
        swatch.classList.add('product-page__color-swatch--unavailable');
        swatch.setAttribute('data-size-unavailable', 'true');
        return;
      }

      // Get variant data for this product
      const variantData = await getProductVariantData(productUrl);
      if (!variantData || !variantData.variants) {
        // If we can't get data, keep current state
        return;
      }

      // Check if there's an available variant with the selected size
      const hasAvailableVariant = variantData.variants.some((variant) => {
        if (!variant.available) return false;
        if (!variant.options || variant.options.length < sizeOptionPosition) return false;
        
        // Check if this variant has the selected size at the size option position
        const variantSize = variant.options[sizeOptionPosition - 1];
        return variantSize && variantSize.toString().trim().toLowerCase() === selectedSize.toString().trim().toLowerCase();
      });

      // Update swatch class
      if (hasAvailableVariant) {
        swatch.classList.remove('product-page__color-swatch--unavailable');
        swatch.removeAttribute('data-size-unavailable');
      } else {
        swatch.classList.add('product-page__color-swatch--unavailable');
        swatch.setAttribute('data-size-unavailable', 'true');
      }
    });

    await Promise.all(updatePromises);
  }

  // Handle variant selection to enable Add to Cart button
  function onVariantChange(event) {
    const productInfo = getMainProductInfoEl();
    if (!productInfo || !event.data || !event.data.variant) return;

    const variant = event.data.variant;
    const productForm = productInfo.querySelector('product-form');

    if (!productForm) return;

    // Always ensure variant input is updated when variant changes
    // This is critical for add to cart to work after product swap
    if (variant && variant.id) {
      if (typeof productInfo.updateVariantInputs === 'function') {
        // Always update variant input to ensure it's set correctly
        productInfo.updateVariantInputs(variant.id);
      } else {
        // Fallback: directly update variant input if method doesn't exist
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id.toString();
          variantInput.removeAttribute('disabled');
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Update color swatch availability based on selected size
    const variantSelects = productInfo.querySelector('product-variant-selects');
    if (variantSelects) {
      const selectedSize = getSelectedSize(variantSelects);
      const currentProductId = productInfo.dataset.productId || 
                               document.querySelector('.product-page__color-swatch--selected')?.dataset.productId ||
                               variantSelects.dataset.currentProductId;
      
      updateColorSwatchAvailability(selectedSize, currentProductId);
    }

    // Enable button if variant is available, disable if unavailable
    if (variant && variant.id && variant.available) {
      if (typeof productForm.toggleSubmitButton === 'function') {
        productForm.toggleSubmitButton(false); // Enable button
      } else {
        const submit = productInfo.querySelector(
          'product-form [type="submit"]'
        );
        if (submit) {
          submit.removeAttribute('disabled');
          submit.removeAttribute('aria-disabled');
        }
      }
      // Clear the "SELECT A SIZE" black override — let the normal enabled styles take over
      const submitBtn = productInfo.querySelector('.product-form__submit');
      if (submitBtn) {
        submitBtn.style.removeProperty('--color-button-background');
        submitBtn.style.removeProperty('--color-button-text');
        submitBtn.style.removeProperty('--color-button-outline');
        submitBtn.style.removeProperty('cursor');
      }
    } else if (variant && variant.id && !variant.available) {
      if (typeof productForm.toggleSubmitButton === 'function') {
        productForm.toggleSubmitButton(
          true,
          window.variantStrings?.soldOut || 'Sold Out'
        );
      } else {
        const submit = productInfo.querySelector(
          'product-form [type="submit"]'
        );
        if (submit) {
          submit.setAttribute('disabled', 'disabled');
          submit.setAttribute('aria-disabled', 'true');
        }
      }
    } else {
      // No variant selected - disable button
      // BUT: Skip this for single-variant products - they don't need variant selection
      const isSingleVariantProduct = productInfo?.dataset.singleVariantProduct === 'true' || 
                                     !productInfo?.querySelector('product-variant-selects');
      if (isSingleVariantProduct) {
        // Single-variant product - don't disable button, it's already handled
        return;
      }
      
      if (typeof productForm.toggleSubmitButton === 'function') {
        productForm.toggleSubmitButton(true, 'SELECT A SIZE');
      } else {
        const submit = productInfo.querySelector(
          'product-form [type="submit"]'
        );
        if (submit) {
          submit.setAttribute('disabled', 'disabled');
          submit.setAttribute('aria-disabled', 'true');
          const span = submit.querySelector('span');
          if (span) span.textContent = 'SELECT A SIZE';
        }
      }
    }
  }

  // Get selected size from variant selects
  function getSelectedSize(variantSelects) {
    if (!variantSelects) return null;
    
    // Find the fieldset with "SELECT A SIZE" legend
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      const legendText = legend?.textContent?.trim() || '';
      
      if (legendText.includes('SELECT A SIZE') || legendText.toUpperCase().includes('SIZE')) {
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          return checkedInput.value;
        }
      }
    }
    
    return null;
  }

  // Handle direct size selection changes
  function onSizeSelectionChange(event) {
    const productInfo = getMainProductInfoEl();
    if (!productInfo || !productInfo.contains(event.target)) return;

    // Check if this is a size selector (radio input in a fieldset)
    const input = event.target;
    if (input.type === 'radio' && input.closest('product-variant-selects')) {
      const variantSelects = productInfo.querySelector('product-variant-selects');
      if (!variantSelects) return;

      // Get the selected size value
      const selectedSize = getSelectedSize(variantSelects);
      const currentProductId = productInfo.dataset.productId || 
                               document.querySelector('.product-page__color-swatch--selected')?.dataset.productId;
      
      // Update color swatches after a short delay to allow variant data to update
      setTimeout(() => {
        updateColorSwatchAvailability(selectedSize, currentProductId);
      }, 100);
    }
  }

  // Initialize
  function init() {
    document.addEventListener('click', onSwatchClick, true);

    // Listen for direct size selection changes
    document.addEventListener('change', onSizeSelectionChange, true);

    // Subscribe to variant change events to enable Add to Cart when size is selected
    if (
      typeof subscribe !== 'undefined' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    } else {
      // Fallback: listen directly to variant selector changes
      document.addEventListener(
        'change',
        (event) => {
          const productInfo = getMainProductInfoEl();
          if (!productInfo || !productInfo.contains(event.target)) return;

          // Check if this is a variant selector
          if (event.target.closest('product-variant-selects')) {
            // Give the component time to update, then check for variant
            setTimeout(() => {
              const selectedVariantEl = productInfo.querySelector(
                'product-variant-selects [data-selected-variant]'
              );
              if (selectedVariantEl && selectedVariantEl.innerHTML) {
                try {
                  const variant = JSON.parse(selectedVariantEl.innerHTML);
                  onVariantChange({ data: { variant } });
                } catch (e) {
                  // Variant not valid JSON yet, wait a bit more
                  setTimeout(() => {
                    try {
                      const variant = JSON.parse(selectedVariantEl.innerHTML);
                      onVariantChange({ data: { variant } });
                    } catch (e2) {
                      // Still not ready, disable button
                      const productForm =
                        productInfo.querySelector('product-form');
                      if (
                        productForm &&
                        typeof productForm.toggleSubmitButton === 'function'
                      ) {
                        productForm.toggleSubmitButton(true, '');
                      }
                    }
                  }, 100);
                }
              } else {
                // No variant selected
                const productForm = productInfo.querySelector('product-form');
                if (
                  productForm &&
                  typeof productForm.toggleSubmitButton === 'function'
                ) {
                  productForm.toggleSubmitButton(true, '');
                }
              }
            }, 50);
          }
        },
        true
      );
    }

    // Enforce no default variant on initial load
    enforceNoDefaultVariant();

    // Prefetch images from all color swatch products
    prefetchAllSwatchProductImages();

    // After product swap completes, the new custom element dispatches this event
    document.addEventListener(
      'product-info:loaded',
      (e) => {
        enforceNoDefaultVariant(e.target);
        // Prefetch images for newly loaded product's related swatches
        setTimeout(() => prefetchAllSwatchProductImages(), 100);
        
        // Clear variant data cache when product is swapped
        variantDataCache.clear();
        
        // Reset color swatch availability states after product swap
        // Check if there's a selected size and update swatches accordingly
        setTimeout(() => {
          const productInfo = getMainProductInfoEl();
          if (!productInfo) return;
          
          const variantSelects = productInfo.querySelector('product-variant-selects');
          if (variantSelects) {
            const selectedSize = getSelectedSize(variantSelects);
            const currentProductId = productInfo.dataset.productId || 
                                   document.querySelector('.product-page__color-swatch--selected')?.dataset.productId;
            updateColorSwatchAvailability(selectedSize, currentProductId);
          }
        }, 200);
      },
      false
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
